import { Drawer } from 'antd';
import { memo, useEffect, useRef, useState } from 'react';

import {
  InstagramPostsCommentsItemModel,
  InstagramQueryPostItemModel,
  InstagramUserSubModel,
} from '@/types/anonymously.interface';
import PostComments from '../PostComments';
import PostContent from '../PostContent';
import useCheckIsMobile from '@/lib/hooks/useCheckIsMobile';
import { InsAccountModel } from '@/types/home';
import { useTranslation } from 'next-i18next';
import PostInfoPannelSkeleton from './PostInfoPannelSkeleton';

interface PostInfoPannelProps {
  functionChild: 'post-comments' | 'post-likes';
  item: InstagramQueryPostItemModel;
  drawerOpen?: boolean;
  onDrawerClose?: () => void;
  getCommnets?: (comments: InstagramPostsCommentsItemModel[]) => void;
  userSubData: InstagramUserSubModel;
  firstGetComments?: (comments: InstagramPostsCommentsItemModel[]) => void;
  alterUpgrade?: () => void;
  notEnoughScoreNotEilte?: () => void;
  notEnoughScoreEilte?: () => void;
  isLogin?: boolean;
  refreshUserData?: () => void;
  setIsChancePost?: (isChancePost: boolean) => void;
  innerAccountInfo?: InsAccountModel;
}

const MIN_DRAWER_HEIGHT = 640;

const PostInfoPannel: React.FC<PostInfoPannelProps> = ({
  functionChild,
  item,
  drawerOpen,
  onDrawerClose,
  getCommnets,
  userSubData,
  firstGetComments,
  alterUpgrade,
  notEnoughScoreNotEilte,
  notEnoughScoreEilte,
  isLogin,
  refreshUserData,
  setIsChancePost,
  innerAccountInfo,
}) => {
  const { isMobile } = useCheckIsMobile();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerHeight, setDrawerHeight] = useState(MIN_DRAWER_HEIGHT);
  // CLS 优化：控制评论区骨架屏显示，等评论加载完成后同帧切换
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const prevPostCodeRef = useRef<string | null>(null);

  const { t } = useTranslation();

  // 当帖子变化时，重置加载状态，显示骨架屏
  useEffect(() => {
    if (item?.code && item.code !== prevPostCodeRef.current) {
      setCommentsLoaded(false);
      prevPostCodeRef.current = item.code;
    }
  }, [item?.code]);

  const handleMobileViewMore = () => {
    // 显示帖子详情弹层
    setIsDrawerOpen(true);
  };

  // 根据页面高度，设置帖子详情弹层高度
  useEffect(() => {
    const handleResize = () => {
      const drawerHeight = window.innerHeight * 0.85;
      setDrawerHeight(drawerHeight < MIN_DRAWER_HEIGHT ? MIN_DRAWER_HEIGHT : drawerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isDrawerOpen && item.code && drawerOpen) {
      setIsDrawerOpen(true);
    }
  }, [item, drawerOpen]);

  // CLS 优化：评论首次加载完成的回调，触发同帧切换
  const handleCommentsFirstLoad = (comments: InstagramPostsCommentsItemModel[]) => {
    // 同帧设置 commentsLoaded，骨架屏和内容在同一帧切换
    setCommentsLoaded(true);
    // 继续调用原有的 firstGetComments 回调
    firstGetComments?.(comments);
  };

  // 渲染帖子评论/帖子点赞
  const renderPostCommentsOrLikes = (preview?: boolean) => {
    const isMobilePreview = isMobile && preview;

    // TODO: 需要优化，避免重复渲染，以及组件重复发起请求
    switch (functionChild) {
      case 'post-comments':
        // CLS 优化：同时渲染骨架屏和评论组件，用 CSS 控制可见性
        // 这样 PostComments 可以在后台加载数据，数据返回后同帧切换
        return (
          <>
            {/* 骨架屏：数据未加载时显示 */}
            <div className={commentsLoaded ? 'hidden' : ''}>
              <PostInfoPannelSkeleton isMobilePreview={isMobilePreview} />
            </div>
            {/* 评论组件：数据加载完成后显示 */}
            <div className={commentsLoaded ? '' : 'hidden'}>
              <PostComments
                item={item}
                isMobilePreview={isMobilePreview}
                maxDisplayCount={isMobilePreview ? 3 : false}
                onLoadMoreClick={isMobilePreview ? handleMobileViewMore : undefined}
                getCommnets={getCommnets}
                userSubData={userSubData}
                firstGetComments={handleCommentsFirstLoad}
                alterUpgrade={alterUpgrade}
                notEnoughScoreNotEilte={notEnoughScoreNotEilte}
                notEnoughScoreEilte={notEnoughScoreEilte}
                isLogin={isLogin}
                refreshUserData={refreshUserData}
                setIsChancePost={setIsChancePost}
                setIsDrawerOpen={setIsDrawerOpen}
                innerAccountInfo={innerAccountInfo}
              />
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {/* 帖子信息，用于PC端 */}
      {!isMobile && (
        <div className="h-[580px] border border-[#D9D9D9] rounded-xl overflow-hidden">
          <div className="h-full flex">
            {/* 帖子内容 */}
            <div className="w-1/2 h-full">
              <PostContent item={item} />
            </div>
            {/* 帖子评论/帖子点赞 */}
            <div className="w-1/2 h-full overflow-auto">{renderPostCommentsOrLikes()}</div>
          </div>
        </div>
      )}
      {/* 帖子信息，用于移动端 */}
      {isMobile && (
        <div>
          {/* 帖子内容 */}
          <PostContent item={item} isMobilePreview onViewMoreClick={handleMobileViewMore} />
          {/* 分割线 */}
          <div className="h-px my-4 bg-[#D9D9D9]"></div>
          {/* 帖子评论/帖子点赞 */}
          {renderPostCommentsOrLikes(true)}
        </div>
      )}
      <div className="p-4 text-sm text-[#0C0C0C] opacity-40 text-center">
        {t('Note: Every click to view replies or load more takes 1 credit.')}
      </div>
      {/* 帖子详情弹层，用于移动端 */}
      <Drawer
        open={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          onDrawerClose?.();
        }}
        placement="bottom"
        height={drawerHeight}
        styles={{
          header: {
            display: 'none',
          },
          body: {
            padding: 0,
          },
        }}
      >
        <div>
          <PostContent item={item} isMobileView />
          <div className="h-px bg-[#D9D9D9]"></div>
          <div className="p-4">{renderPostCommentsOrLikes()}</div>
        </div>
      </Drawer>
    </>
  );
};

export default memo(PostInfoPannel);
